import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Tabs, Card, message, Button, Modal, Form, Input, InputNumber, Space, Select, Collapse, Progress, Tag, Row, Col, Spin, Table, Popconfirm, Pagination, Checkbox, Dropdown, Menu, Divider } from 'antd';
import { PlusOutlined, HistoryOutlined, BarChartOutlined, AimOutlined, LineChartOutlined, EditOutlined, DeleteOutlined, CheckCircleOutlined, CloseCircleOutlined, CheckOutlined, CloseOutlined, DownloadOutlined, DownOutlined } from '@ant-design/icons';
import DataTable from './DataTable';
import PhdReviewMatrix, { PhdBatchMatrix, groupPhdSubmissionsByIndicatorBatch } from './PhdReviewMatrix';
import PhdExportMatrixPreview from './PhdExportMatrixPreview';
import PhdMatrixEntry from './PhdMatrixEntry';
import EditableDataTable from './EditableDataTable';
import EditableTargetTable from './EditableTargetTable';
import PhdTargetMatrix from './PhdTargetMatrix';
import DataChart from './DataChart';
import { groupSubmissionsByBatch } from '../utils/submissionUtils';
import api, { getApiBaseURL } from '../utils/api';
import { getToken } from '../utils/auth';
import moment from 'moment';
import { formatDateTime } from '../utils/timeUtils';

import YearInput from './shared/YearInput';
import DateInput from './shared/DateInput';
import DateRangeInput from './shared/DateRangeInput';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import RejectModal from './shared/RejectModal';
import IndicatorChartBase from './shared/IndicatorChart';
import TargetHistoryTable from './shared/TargetHistoryTable';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);


const { TabPane } = Tabs;
const { Panel } = Collapse;

// Define record-type data types (columns can be selected for statistics)
const recordDataTypes = [
  'research_teams',      // Provincial-level and above research teams
  'teaching_teams',      // Provincial-level and above teaching teams
  'research_papers',     // Representative academic papers and monographs published in recent 5 years
  'research_awards',     // Representative scientific research awards received in recent 5 years
  'teaching_awards',     // Provincial-level and above teaching achievement awards received in recent 5 years
  'research_projects',   // Representative research projects undertaken in recent 5 years
  'research_platforms',  // Representative key labs, bases, centers, and other platforms
  'teaching_platforms',  // Provincial-level and above teaching platforms
  'degree_application',  // Application for new degree authorization
  'degree_points',       // Current degree program details
  'graduate_teaching_awards', // Graduate school teaching achievement awards
  'graduate_courses',    // Graduate school representative courses and programs
  'graduate_student_achievements', // Representative achievements of enrolled students
  'graduate_platforms'   // Graduate school platforms
];

import { getDataForms, PHD_TYPES } from '../utils/dataForms';

const DepartmentView = ({ department, user, initialTab }) => {
  // Only super admins (admins not affiliated with any department); departments have regular users
  const isSuperAdmin = user?.role === 'admin' && !user?.department_id;
  
  const [activeTab, setActiveTab] = useState(initialTab || 'indicators');
  // Use useMemo to ensure dataForms recalculates when department changes
  const dataForms = useMemo(() => {
    if (!department || !department.name) {
      console.warn('DepartmentView: department or department.name is missing', department);
      return [];
    }
    const forms = getDataForms(department.name);
    console.log('DepartmentView: dataForms for', department.name, forms);
    return forms;
  }, [department?.name]);

  const isPhdDepartment = useMemo(
    () => !!department && dataForms.some(f => PHD_TYPES.includes(f.key)),
    [department, dataForms]
  );

  // Note: Record-type data now also supports statistics by category or numeric fields, so always show the statistics tab
  // No longer need to check if all data is record-type
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [currentForm, setCurrentForm] = useState(null);
  const [form] = Form.useForm();
  const [indicatorTargets, setIndicatorTargets] = useState([]);
  const [targetRecords, setTargetRecords] = useState([]); // Store all target records (including IDs)
  // const [targetModalVisible, setTargetModalVisible] = useState(false); // Removed, changed to page tab
  const [targetForm] = Form.useForm();
  const [editingTargetId, setEditingTargetId] = useState(null); // Target ID currently being edited
  const [activeTargetTab, setActiveTargetTab] = useState('target'); // Currently active tab
  const [currentTargetFilter, setCurrentTargetFilter] = useState(null); // Current filter conditions (data_type, field_key, etc.)
  const [departmentIndicators, setDepartmentIndicators] = useState(null);
  const [indicatorsLoading, setIndicatorsLoading] = useState(false);
  const [selectedTargetYear, setSelectedTargetYear] = useState(new Date().getFullYear()); // Selected year, defaults to current year
  // Data statistics tab initial selection (used for navigating from indicators)
  const [statisticsInitialDataType, setStatisticsInitialDataType] = useState(null);
  const [statisticsInitialFieldKey, setStatisticsInitialFieldKey] = useState(null);
  const [statisticsInitialCategoryValue, setStatisticsInitialCategoryValue] = useState(null);
  // Review-related state
  const [reviewStatusFilter, setReviewStatusFilter] = useState('pending'); // Review status filter: pending, approved, rejected, all
  const [reviewBatchPage, setReviewBatchPage] = useState(1); // Review batch pagination page
  const [reviewBatchPageSize, setReviewBatchPageSize] = useState(10); // Review batch pagination page size
  const [reviewing, setReviewing] = useState(false); // Whether currently reviewing
  // Review reject questionnaire related state (department view)
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [rejectSubmitting, setRejectSubmitting] = useState(false);
  const [rejectTargetIds, setRejectTargetIds] = useState([]);
  const [rejectTargetSubmissions, setRejectTargetSubmissions] = useState([]);
  const [rejectForm] = Form.useForm();
  // Row-level checkbox selection in the “Review” tab within the department view
  const [reviewSelectedRowKeys, setReviewSelectedRowKeys] = useState([]);
  
  // Get current date parts as defaults (year, month, day only)
  const getCurrentDateParts = () => {
    const now = moment();
    return {
      submitted_year: now.year(),
      submitted_month: now.month() + 1, // moment months are 0-indexed
      submitted_day: now.date()
    };
  };

  useEffect(() => {
    if (department && department.id) {
      console.log('DepartmentView: Loading submissions for department', department.id);
      loadSubmissions();
      loadIndicatorTargets();
      loadDepartmentIndicators();
      
      // If initialTab is provided, set it as the active tab
      if (initialTab) {
        setActiveTab(initialTab);
      } else if (user?.role === 'admin' && activeTab === 'input' && !isPhdDepartment) {
        // If admin is currently on the "Data Entry" tab, switch to "Department Indicators" (admins should not see data entry)
        setActiveTab('indicators');
      }
    }
  }, [department, user, initialTab, isPhdDepartment]);

  useEffect(() => {
    if (!department || !isPhdDepartment) return;
    setActiveTab((t) => (t === 'indicators' ? (isSuperAdmin ? 'review' : 'input') : t));
  }, [department?.id, isPhdDepartment, isSuperAdmin]);

  const loadDepartmentIndicators = async () => {
    if (!department || !department.id) return;
    setIndicatorsLoading(true);
    try {
      const response = await api.get(`/indicators/${department.id}`);
      setDepartmentIndicators(response.data);
    } catch (error) {
      console.error('加载部门指标失败:', error);
      message.error('加载部门指标失败');
      setDepartmentIndicators(null);
    } finally {
      setIndicatorsLoading(false);
    }
  };

  const loadIndicatorTargets = async () => {
    if (!department || !department.id) return;
    try {
      const response = await api.get('/indicator-targets', {
        params: { department_id: department.id }
      });
      const targets = response.data || [];
      
      // Save all target records (including IDs)
      setTargetRecords(targets);
      
      // Group by data type and field for progress display
      setIndicatorTargets(targets);
      
      console.log('部门目标记录已加载:', targets.length, '条记录');
    } catch (error) {
      console.error('加载指标目标失败:', error);
      setTargetRecords([]);
      setIndicatorTargets([]);
    }
  };

  const loadSubmissions = async () => {
    setLoading(true);
    try {
      // Do not pass status parameter, get data for all statuses (including pending, approved, rejected)
      const response = await api.get('/submissions', {
        params: { department_id: department.id }
      });
      // Filter out data items containing the _replaced_by field (overwritten data)
      const filteredData = (response.data || []).filter(sub => {
        if (!sub.data_content) return true;
        const content = typeof sub.data_content === 'string' 
          ? JSON.parse(sub.data_content) 
          : sub.data_content;
        return !content || !content._replaced_by;
      });
      // Sort in descending order by submission time (newest first)
      filteredData.sort((a, b) => {
        const timeA = a.submitted_at ? new Date(a.submitted_at).getTime() : 0;
        const timeB = b.submitted_at ? new Date(b.submitted_at).getTime() : 0;
        return timeB - timeA;
      });
      console.log(`DepartmentView: 加载了 ${filteredData.length} 条记录，部门ID: ${department.id}`);
      const pendingCount = filteredData.filter(s => s.status === 'pending').length;
      const approvedCount = filteredData.filter(s => s.status === 'approved').length;
      const rejectedCount = filteredData.filter(s => s.status === 'rejected').length;
      console.log(`DepartmentView: 待审核: ${pendingCount}, 已通过: ${approvedCount}, 已拒绝: ${rejectedCount}`);
      setSubmissions(filteredData);
    } catch (error) {
      console.error('加载数据失败:', error);
      message.error('加载数据失败');
    } finally {
      setLoading(false);
    }
  };

  // Review a single record
  const handleReview = async (id, status, silent = false, extraData = {}) => {
    try {
      await api.post(`/submissions/${id}/review`, { status, ...extraData });
      if (!silent) {
        message.success(status === 'approved' ? '已通过审核' : '已拒绝');
      }
      loadSubmissions();
    } catch (error) {
      if (!silent) {
        message.error(error.response?.data?.error || '审核失败');
      }
      throw error;
    }
  };

  // No longer review by entire batch; unified approach using row-level selection (handleApproveSelectedInReview / handleRejectSelectedInReview)

  // In the “Review” tab, use the currently selected rows to batch approve
  const handleApproveSelectedInReview = async () => {
    if (!reviewSelectedRowKeys || reviewSelectedRowKeys.length === 0) {
      message.warning('请先勾选要通过的记录');
      return;
    }

    const pendingSelected = submissions.filter(
      (s) => s.status === 'pending' && reviewSelectedRowKeys.includes(s.id)
    );

    if (pendingSelected.length === 0) {
      message.warning('选中的记录中没有待审核的数据');
      return;
    }

    setReviewing(true);
    try {
      const response = await api.post('/submissions/batch-review', {
        submission_ids: pendingSelected.map((s) => s.id),
        status: 'approved'
      });

      if (response.data && response.data.updated_count > 0) {
        message.success(`已通过 ${response.data.updated_count} 条选中记录`);
      } else {
        message.warning('批量通过完成，但未更新任何记录');
      }

      await loadSubmissions();
      setReviewSelectedRowKeys([]);
    } catch (error) {
      console.error('部门审核：批量通过选中记录失败:', error);
      message.error(error.response?.data?.error || '批量通过选中记录失败');
    } finally {
      setReviewing(false);
    }
  };

  // In the "Review" tab, use the currently selected rows to batch reject (opens reject questionnaire)
  const handleRejectSelectedInReview = () => {
    if (!reviewSelectedRowKeys || reviewSelectedRowKeys.length === 0) {
      message.warning('请先勾选要拒绝的记录');
      return;
    }

    const pendingSelected = submissions.filter(
      (s) => s.status === 'pending' && reviewSelectedRowKeys.includes(s.id)
    );

    if (pendingSelected.length === 0) {
      message.warning('选中的记录中没有待审核的数据');
      return;
    }

    const pendingIds = pendingSelected.map((s) => s.id);
    // Save selected data rows for display in the modal
    setRejectTargetSubmissions(pendingSelected);
    setRejectTargetIds(pendingIds);
    rejectForm.resetFields();
    setRejectModalVisible(true);
  };

  // Submit reject questionnaire
  const handleRejectSubmit = async () => {
    try {
      const values = await rejectForm.validateFields();
      let { reason, detail } = values;
      // If multi-select reasons, merge into a string
      if (Array.isArray(reason)) {
        reason = reason.join('；');
      }
      const extraData = { reason, detail };

      setRejectSubmitting(true);
      // Decide single or batch API based on count
      if (rejectTargetIds.length === 1) {
        await handleReview(rejectTargetIds[0], 'rejected', true, extraData);
        message.success('已拒绝该记录');
      } else {
        const response = await api.post('/submissions/batch-review', {
          submission_ids: rejectTargetIds,
          status: 'rejected',
          ...extraData
        });
        if (response.data && response.data.updated_count > 0) {
          message.success(`成功拒绝 ${response.data.updated_count} 条记录`);
        } else {
          message.warning('批量拒绝完成，但未更新任何记录');
        }
      }

      // Refresh data
      await loadSubmissions();

      // Clear selection state
      setReviewSelectedRowKeys([]);
      setRejectModalVisible(false);
      setRejectTargetIds([]);
      setRejectTargetSubmissions([]);
    } catch (error) {
      if (error && error.errorFields) {
        // Form validation error, do not show prompt
        return;
      }
      console.error('提交拒绝问卷失败:', error);
      message.error(error.response?.data?.error || '提交拒绝信息失败');
    } finally {
      setRejectSubmitting(false);
    }
  };

  const handleAddData = (formConfig) => {
    setCurrentForm(formConfig);
    form.resetFields();
    // Set submission time initial value to current date parts (year, month, day only)
    form.setFieldsValue(getCurrentDateParts());
    setModalVisible(true);
  };

  const handleSubmit = async (values) => {
    try {
      // Process computed fields
      const dataContent = { ...values };
      currentForm.fields.forEach(field => {
        if (field.computed && field.formula) {
          dataContent[field.key] = field.formula(dataContent);
        }
        // Handle date fields
        if (field.type === 'date' && values[field.key]) {
          dataContent[field.key] = moment(values[field.key]).format('YYYY-MM-DD');
        }
        // Handle date range fields
        if (field.type === 'daterange' && values[field.key]) {
          const [start, end] = values[field.key];
          dataContent[field.key] = `${moment(start).format('YYYY-MM-DD')} 至 ${moment(end).format('YYYY-MM-DD')}`;
        }
        // Handle year field (if select type)
        if ((field.key === 'year' || field.key === '年份') && values[field.key]) {
          // Year is already in values, use directly
        }
      });

      // Process submission time: extract year, month, day from form and combine
      let submittedAt = null;
      if (values.submitted_year && values.submitted_month !== undefined && values.submitted_day !== undefined) {
        // Combine into YYYY-MM-DD format, time portion uses 00:00:00
        const dateStr = `${values.submitted_year}-${String(values.submitted_month).padStart(2, '0')}-${String(values.submitted_day).padStart(2, '0')}`;
        submittedAt = moment(`${dateStr} 00:00:00`, 'YYYY-MM-DD HH:mm:ss').toISOString();
      }

      // Determine submission year: prefer form year field, then extract from data content, finally use current year
      let submissionYear = values.year || values['年份'];
      if (!submissionYear && (dataContent.year || dataContent['年份'])) {
        submissionYear = dataContent.year || dataContent['年份'];
      }
      if (!submissionYear) {
        submissionYear = new Date().getFullYear();
      }

      await api.post('/submissions', {
        department_id: department.id,
        data_type: currentForm.key,
        data_content: dataContent,
        year: submissionYear,
        submitted_at: submittedAt
      });

      message.success('提交成功，等待审核');
      form.resetFields();
      setModalVisible(false);
      setCurrentForm(null);
      loadSubmissions();
      loadIndicatorTargets(); // Reload indicator targets to update progress
    } catch (error) {
      message.error(error.response?.data?.error || '提交失败');
    }
  };

  // Map indicator key to data source (data_type, field_key, field_type, category_value)
  const getIndicatorDataSource = (indicatorKey) => {
    const dataSourceMap = {
      'fulltime_teachers_count': { data_type: 'teacher_count', field_key: '专任教师总人数', field_type: 'number' },
      'senior_title_count': { data_type: 'teacher_count', field_key: '专任教师正高数量', field_type: 'number' },
      'associate_title_count': { data_type: 'teacher_count', field_key: '专任教师副高数量', field_type: 'number' },
      'undergraduate_count': { data_type: 'student_count', field_key: '本科人数', field_type: 'number' },
      'college_count': { data_type: 'student_count', field_key: '专科人数', field_type: 'number' },
      'research_awards': { data_type: 'research_awards', field_key: '奖励类别', field_type: 'category', category_value: '国家级奖励' },
      'provincial_awards': { data_type: 'research_awards', field_key: '奖励类别', field_type: 'category', category_value: '省部级奖励' },
      'industry_awards': { data_type: 'research_awards', field_key: '奖励类别', field_type: 'category', category_value: '行业奖励' },
      'national_projects': { data_type: 'research_projects', field_key: '类别', field_type: 'category', category_value: '国家级项目' },
      'provincial_projects': { data_type: 'research_projects', field_key: '类别', field_type: 'category', category_value: '省部级项目' },
      'national_research_platforms': { data_type: 'research_platforms', field_key: '批准部门', field_type: 'category', category_value: '国家级' },
      'provincial_research_platforms': { data_type: 'research_platforms', field_key: '批准部门', field_type: 'category', category_value: '省部级' },
      'national_teaching_awards': { data_type: 'teaching_awards', field_key: '奖励类别', field_type: 'category', category_value: '国家级教学成果奖' },
      'provincial_teaching_awards': { data_type: 'teaching_awards', field_key: '奖励类别', field_type: 'category', category_value: '省级教学成果奖' },
      'national_teaching_platforms': { data_type: 'teaching_platforms', field_key: '类别', field_type: 'category', category_value: '国家级' },
      'provincial_teaching_platforms': { data_type: 'teaching_platforms', field_key: '类别', field_type: 'category', category_value: '省部级' },
      'phd_supervisors': { data_type: 'degree_points', field_key: '授权级别类型', field_type: 'category', category_value: '博士' },
      'phd_graduates': { data_type: 'graduate_students', field_key: '博士研究生', field_type: 'number' },
      'master_students': { data_type: 'graduate_students', field_key: '硕士研究生', field_type: 'number' },
      'graduate_platforms': { data_type: 'graduate_platforms', field_key: '名称', field_type: 'count' },
      'international_students_count': { data_type: 'international_students', field_key: '留学生人数', field_type: 'number' },
      'cooperation_institutions': { data_type: 'international_exchange', field_key: '中外合作办学机构数', field_type: 'number' },
      'cooperation_projects': { data_type: 'international_exchange', field_key: '中外合作办学项目数', field_type: 'number' },
      'annual_funding': { data_type: 'research_funding', field_key: '经费情况(万元)', field_type: 'number' },
    };
    return dataSourceMap[indicatorKey];
  };

  // Check if indicator has data for the specified year
  const hasIndicatorDataForYear = (indicator, year) => {
    const dataSource = getIndicatorDataSource(indicator.key);
    if (!dataSource) return false;
    
    // Use calculateCurrentValue to check if the indicator has data for that year
    const value = calculateCurrentValue(
      dataSource.data_type,
      dataSource.field_key,
      year,
      dataSource.field_type,
      dataSource.category_value
    );
    
    // For numeric fields, 0 is also a valid value
    return value !== null && value !== undefined;
  };

  // Check if a batch is fully approved (all records in batch have approved status)
  const isBatchFullyApproved = (batchSubmissions) => {
    if (!batchSubmissions || batchSubmissions.length === 0) return false;
    return batchSubmissions.every(sub => sub.status === 'approved');
  };
  
  // Get display label by data type
  const getDataTypeLabel = (dataType) => {
    const formConfig = dataForms.find(f => f.key === dataType);
    return formConfig ? formConfig.label : dataType;
  };

  // ── Review tab rendering ──

  const renderReviewContent = () => {
    let filteredSubmissions = [];
    if (reviewStatusFilter !== 'all') {
      filteredSubmissions = submissions.filter(s => s.status === reviewStatusFilter);
    } else {
      filteredSubmissions = submissions;
    }

    const batches = filteredSubmissions.length === 0
      ? []
      : groupSubmissionsByBatch(filteredSubmissions);

    const start = (reviewBatchPage - 1) * reviewBatchPageSize;
    const end = start + reviewBatchPageSize;
    const paginatedBatches = batches.slice(start, end);

    if (dataForms && dataForms.length > 0) {
      if (isPhdDepartment) {
        return (
          <>
            {filteredSubmissions.length === 0 ? (
              <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
                <p>暂无{reviewStatusFilter === 'all' ? '' : reviewStatusFilter === 'approved' ? '已通过' : reviewStatusFilter === 'rejected' ? '已拒绝' : '待审核'}的记录</p>
              </div>
            ) : (
              <>
                {reviewStatusFilter === 'pending' && !isPhdDepartment && (
                  <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'flex-start' }}>
                    <Dropdown
                      overlay={
                        <Menu
                          onClick={({ key }) => {
                            if (key === 'approve') handleApproveSelectedInReview();
                            else if (key === 'reject') handleRejectSelectedInReview();
                          }}
                        >
                          <Menu.Item key="approve" icon={<CheckOutlined />}>通过选中行</Menu.Item>
                          <Menu.Item key="reject" icon={<CloseOutlined />}>拒绝选中行</Menu.Item>
                        </Menu>
                      }
                      trigger={['click']}
                    >
                      <Button loading={reviewing}>批量操作 <DownOutlined /></Button>
                    </Dropdown>
                  </div>
                )}
                <PhdReviewMatrix
                  deptName={department.name}
                  submissions={filteredSubmissions}
                  onCellReview={loadSubmissions}
                />
              </>
            )}
          </>
        );
      }

      return (
        <>
          {batches.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
              <p>暂无{reviewStatusFilter === 'all' ? '' : reviewStatusFilter === 'approved' ? '已通过' : reviewStatusFilter === 'rejected' ? '已拒绝' : '待审核'}的记录</p>
            </div>
          ) : (
            <>
              {reviewStatusFilter === 'pending' && (
                <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'flex-start' }}>
                  <Dropdown
                    key="batch-operation-review"
                    overlay={
                      <Menu
                        onClick={({ key }) => {
                          if (key === 'approve') {
                            handleApproveSelectedInReview();
                          } else if (key === 'reject') {
                            handleRejectSelectedInReview();
                          }
                        }}
                        className="batch-operation-dropdown"
                      >
                        <Menu.Item key="approve" icon={<CheckOutlined />}>
                          通过选中行
                        </Menu.Item>
                        <Menu.Item key="reject" icon={<CloseOutlined />}>
                          拒绝选中行
                        </Menu.Item>
                      </Menu>
                    }
                    trigger={['click']}
                    className="batch-operation-dropdown"
                    getPopupContainer={(triggerNode) => triggerNode.parentElement}
                  >
                    <Button
                      key="batch-operation-button-review"
                      className="batch-operation-dropdown"
                      loading={reviewing}
                    >
                      批量操作 <DownOutlined />
                    </Button>
                  </Dropdown>
                </div>
              )}
              <Collapse
                defaultActiveKey={[]}
                style={{ background: '#fff' }}
                items={paginatedBatches.map((batch) => {
                  const formConfig = dataForms.find(f => f.key === batch.data_type);
                  return {
                    key: batch.key,
                    label: (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                          <span style={{ fontWeight: 'bold' }}>
                            {getDataTypeLabel(batch.data_type)}
                          </span>
                          <Tag color="blue">{batch.submissions.length} 条数据</Tag>
                          {batch.status && (
                            <Tag color={batch.status === 'approved' ? 'green' : batch.status === 'rejected' ? 'red' : 'orange'}>
                              {batch.status === 'approved' ? '已通过' : batch.status === 'rejected' ? '已拒绝' : '待审核'}
                            </Tag>
                          )}
                          {batch.submitted_by_username && (
                            <Tag color="green">提交者: {batch.submitted_by_username}</Tag>
                          )}
                          {batch.submitted_at && (
                            <span style={{ color: '#666', fontSize: '12px' }}>
                              {formatDateTime(batch.submitted_at)}
                            </span>
                          )}
                        </div>
                      </div>
                    ),
                    children: (
                      <div>
                        {formConfig ? (
                          <DataTable
                            dataType={formConfig.key}
                            submissions={batch.submissions}
                            fields={formConfig.fields}
                            onRefresh={loadSubmissions}
                            user={user}
                            departmentId={department.id}
                            disableBatchGrouping={true}
                            rowSelection={
                              reviewStatusFilter === 'pending'
                                ? {
                                    selectedRowKeys: reviewSelectedRowKeys,
                                    onChange: (keys) => setReviewSelectedRowKeys(keys),
                                    getCheckboxProps: (record) => ({
                                      disabled: record.status === 'approved' || record.status === 'rejected'
                                    })
                                  }
                                : undefined
                            }
                          />
                        ) : (
                          <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                            <p>未找到数据类型配置</p>
                          </div>
                        )}
                      </div>
                    )
                  };
                })}
              />
              <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'center' }}>
                <Pagination
                  current={reviewBatchPage}
                  pageSize={reviewBatchPageSize}
                  total={batches.length}
                  onChange={(page, pageSize) => {
                    setReviewBatchPage(page);
                    setReviewBatchPageSize(pageSize);
                  }}
                  onShowSizeChange={(current, size) => {
                    setReviewBatchPage(1);
                    setReviewBatchPageSize(size);
                  }}
                  showSizeChanger
                  showTotal={(total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 个批次`}
                  pageSizeOptions={['10', '20', '50', '100']}
                />
              </div>
            </>
          )}
        </>
      );
    } else {
      return (
        <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
          <p>该部门暂无数据表单配置</p>
        </div>
      );
    }
  };

  // Define data types categorized by year (these have only one record per year, need to filter by year)
  const yearBasedDataTypes = [
    'research_funding',      // Research funding receipt status
    'teacher_count',         // Basic condition data
    'student_count',         // Full-time enrolled student count
    'international_students', // Full-time enrolled international student count
    'graduate_students',      // Full-time graduate student count
    ...(isPhdDepartment
      ? [
          'discipline_team',
          'talent_training',
          'scientific_research',
          'academic_exchange'
        ]
      : [])
  ];

  // Filter data: keep only the latest approved record per year (excluding overwritten records)
  // For year-based data types, keep only the latest record per year
  // For list-type data types, return all approved records
  const filterLatestByYear = (submissionsList, dataType) => {
    if (!submissionsList || submissionsList.length === 0) {
      return [];
    }

    // Keep only approved records
    const approvedOnly = submissionsList.filter(s => s.status === 'approved');
    
    // If not a year-based data type, return all approved records directly
    if (!dataType || !yearBasedDataTypes.includes(dataType)) {
      return approvedOnly;
    }
    
    // Year-based data types: group by year, keep only the latest record per year
    const yearGroups = {};
    
    approvedOnly.forEach(sub => {
      // Extract year
      let year = null;
      
      // Prefer sub.year field
      if (sub.year && sub.year > 0) {
        year = sub.year;
      } else {
        // Extract year from data_content
        try {
          const content = typeof sub.data_content === 'string' 
            ? JSON.parse(sub.data_content) 
            : (sub.data_content || {});
          const yearValue = content.year || content['年份'];
          if (yearValue) {
            year = typeof yearValue === 'string' ? parseInt(yearValue, 10) : (typeof yearValue === 'number' ? Math.floor(yearValue) : null);
            if (isNaN(year) || year <= 0 || year < 1900 || year > 2100) {
              year = null;
            }
          }
        } catch (e) {
          // Parse failed, skip
        }
      }
      
      // Only process records with valid years
      if (year && year > 0) {
        if (!yearGroups[year]) {
          yearGroups[year] = [];
        }
        yearGroups[year].push(sub);
      }
    });
    
    // For each year, keep only the latest record
    const latestSubmissions = [];
    Object.keys(yearGroups).forEach(year => {
      const yearSubs = yearGroups[year];
      if (yearSubs.length === 1) {
        latestSubmissions.push(yearSubs[0]);
      } else {
        // Sort by review time descending, fall back to submission time descending
        const sorted = yearSubs.sort((a, b) => {
          const aTime = a.reviewed_at 
            ? new Date(a.reviewed_at).getTime() 
            : (a.submitted_at ? new Date(a.submitted_at).getTime() : 0);
          const bTime = b.reviewed_at 
            ? new Date(b.reviewed_at).getTime() 
            : (b.submitted_at ? new Date(b.submitted_at).getTime() : 0);
          return bTime - aTime; // Descending, newest first
        });
        // Keep only the latest record
        latestSubmissions.push(sorted[0]);
      }
    });
    
    return latestSubmissions;
  };
  
  // Calculate current value (only using batch-approved data)
  const calculateCurrentValue = (dataType, fieldKey, targetYear, fieldType, categoryValue) => {
    // Ensure targetYear is a number type
    const numTargetYear = typeof targetYear === 'string' ? parseInt(targetYear, 10) : (typeof targetYear === 'number' ? Math.floor(targetYear) : targetYear);
    
    // Group by batch first
    const batches = groupSubmissionsByBatch(submissions);
    
    // Keep only data from fully approved batches
    const approvedBatchSubmissions = [];
    batches.forEach(batch => {
      if (isBatchFullyApproved(batch.submissions)) {
        approvedBatchSubmissions.push(...batch.submissions);
      }
    });
    
    const relevantSubmissions = approvedBatchSubmissions.filter(s => {
      if (s.data_type !== dataType) return false;
      
      // Prefer s.year field; if null/undefined/0, extract year from data_content
      let sYear = typeof s.year === 'string' ? parseInt(s.year, 10) : (typeof s.year === 'number' ? Math.floor(s.year) : s.year);
      
      // If year field is invalid, extract year from data_content
      if (!sYear || isNaN(sYear) || sYear <= 0) {
        try {
          const content = typeof s.data_content === 'string' ? JSON.parse(s.data_content) : (s.data_content || {});
          const yearValue = content.year || content['年份'];
          if (yearValue) {
            sYear = typeof yearValue === 'string' ? parseInt(yearValue, 10) : (typeof yearValue === 'number' ? Math.floor(yearValue) : yearValue);
          }
        } catch (e) {
          // Parse failed, skip this record
          return false;
        }
      }
      
      // Exclude invalid years (null, undefined, 0, or NaN)
      if (!sYear || isNaN(sYear) || sYear <= 0) return false;
      return sYear === numTargetYear;
    });
    
    if (relevantSubmissions.length === 0) return null;
    
    // If multiple records for same year, use the latest approved one (sorted by reviewed_at or submitted_at)
    let latestSubmission = relevantSubmissions[0];
    if (relevantSubmissions.length > 1) {
      // Sort by review time descending, fall back to submission time descending
      latestSubmission = relevantSubmissions.sort((a, b) => {
        const aTime = a.reviewed_at ? new Date(a.reviewed_at).getTime() : (a.submitted_at ? new Date(a.submitted_at).getTime() : 0);
        const bTime = b.reviewed_at ? new Date(b.reviewed_at).getTime() : (b.submitted_at ? new Date(b.submitted_at).getTime() : 0);
        return bTime - aTime; // Descending
      })[0];
    }
    
    // Only use the latest record for calculation
    const finalSubmissions = [latestSubmission];
    
    // If category field, count occurrences of the category value
    if (fieldType === 'category' && categoryValue) {
      let count = 0;
      finalSubmissions.forEach(sub => {
        // Parse data_content (may be string or object)
        let content = sub.data_content;
        if (typeof content === 'string') {
          try {
            content = JSON.parse(content);
          } catch (e) {
            console.error('解析data_content失败:', e);
            content = {};
          }
        }
        content = content || {};
        
        const value = content[fieldKey];
        if (value !== null && value !== undefined) {
          const valueStr = String(value);
          // For research platforms, determine by approving department
          if (dataType === 'research_platforms' && fieldKey === '批准部门') {
            if (categoryValue === '国家级' && (valueStr.includes('国家') || valueStr.includes('部委'))) {
              count++;
            } else if (categoryValue === '省部级' && (valueStr.includes('省') || valueStr.includes('市'))) {
              count++;
            }
          } else if (dataType === 'research_projects' && fieldKey === '类别') {
            // For research projects, need to check if it contains categoryValue
            if (categoryValue === '国家级项目' && valueStr.includes('国家级')) {
              count++;
            } else if (categoryValue === '省部级项目' && (valueStr.includes('省部级') || valueStr.includes('省级'))) {
              count++;
            }
          } else if (dataType === 'degree_points' && fieldKey === '授权级别类型') {
            // For degree programs, need to check if it contains "PhD"
            if (categoryValue === '博士' && (valueStr.includes('博士') || valueStr.toUpperCase().includes('PHD'))) {
              count++;
            }
          } else {
            // Other cases, match directly
            if (value === categoryValue || valueStr === String(categoryValue)) {
              count++;
            }
          }
        }
      });
      return count > 0 ? count : 0;
    }
    
    // If numeric field, directly use the latest record value (no accumulation)
    // Special handling: if field_type is count, count records (should always be 1 since only using latest)
    if (fieldType === 'count') {
      return 1; // Only using latest record, so count is 1
    }
    
    // Only use the latest record value, do not accumulate
    let total = 0;
    let hasValue = false;
    finalSubmissions.forEach(sub => {
      // Parse data_content (may be string or object)
      let content = sub.data_content;
      if (typeof content === 'string') {
        try {
          content = JSON.parse(content);
        } catch (e) {
          console.error('解析data_content失败:', e);
          content = {};
        }
      }
      content = content || {};
      
      const value = content[fieldKey];
      if (typeof value === 'number') {
        total += value;
        hasValue = true;
      }
    });
    
    // Unit conversion: some fields need conversion from ten-thousands to hundreds of millions
    
    // If value exists (including 0), return total; otherwise return null
    return hasValue ? total : null;
  };

  // Handle indicator target editing (new or edit existing target)
  const handleEditTarget = (targetId = null, filter = null) => {
    setEditingTargetId(targetId);
    setCurrentTargetFilter(filter);
    setActiveTargetTab('target');
    
    if (targetId) {
      // Edit existing target
      const target = targetRecords.find(t => t.id === targetId);
      if (target) {
        targetForm.setFieldsValue({
          data_type: target.data_type,
          field_key: target.field_key,
          field_label: target.field_label,
          field_type: target.field_type || 'number',
          category_value: target.category_value || null,
          target_value: target.target_value,
          target_year: target.target_year,
          start_year: target.start_year,
          end_year: target.end_year
        });
      }
    } else if (filter) {
      // Create new target, pre-fill using filter conditions
      targetForm.setFieldsValue({
        data_type: filter.data_type,
        field_key: filter.field_key,
        field_label: filter.field_label,
        field_type: filter.field_type || 'number',
        category_value: filter.category_value || null,
        target_value: 0,
        target_year: new Date().getFullYear() + 1,
        start_year: new Date().getFullYear(),
        end_year: new Date().getFullYear() + 1
      });
    }
    // setTargetModalVisible(true); // Removed, changed to page tab
    setActiveTab('targets'); // Switch to target management tab
  };

  // Handle indicator target submission
  const handleTargetSubmit = async (values) => {
    if (!department || !department.id) return;
    
    try {
      const targetData = {
        department_id: department.id,
        data_type: values.data_type,
        field_key: values.field_key,
        field_label: values.field_label,
        field_type: values.field_type || 'number',
        category_value: values.category_value || null,
        target_value: values.target_value,
        target_year: values.start_year || values.target_year, // Backward compatibility
        start_year: values.start_year,
        end_year: values.end_year
      };
      
      if (editingTargetId) {
        // Update existing target
        await api.put(`/indicator-targets/${editingTargetId}`, {
          target_value: values.target_value,
          target_year: values.start_year || values.target_year, // Backward compatibility
          start_year: values.start_year,
          end_year: values.end_year,
          field_label: values.field_label,
          category_value: values.category_value || null
        });
        message.success('指标目标更新成功');
        // setTargetModalVisible(false); // Removed, changed to page tab
        targetForm.resetFields();
        setEditingTargetId(null);
        setCurrentTargetFilter(null);
        setActiveTargetTab('target');
      } else {
        // Create new target
      await api.post('/indicator-targets', targetData);
      message.success('指标目标设置成功');
        // Do not auto-close modal, switch to history tab
        setActiveTargetTab('history');
      targetForm.resetFields();
        setEditingTargetId(null);
      }
      
      // Reload targets
      await loadIndicatorTargets();
    } catch (error) {
      console.error('设置目标失败:', error);
      message.error(error.response?.data?.error || '设置失败');
    }
  };
  
  // Delete target
  const handleDeleteTarget = async (targetId) => {
    try {
      await api.delete(`/indicator-targets/${targetId}`);
      message.success('目标删除成功');
      await loadIndicatorTargets();
    } catch (error) {
      console.error('删除目标失败:', error);
      message.error(error.response?.data?.error || '删除失败');
    }
  };

  if (!department) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>请选择部门</p>
        </div>
      </Card>
    );
  }

  // Determine whether to show the data entry tab
  // Only regular users (non-admin) can see the data entry tab
  const canInputData = user?.role !== 'admin';
  // Determine whether user can manage indicator targets (admin only)
  const canManageTargets = user?.role === 'admin';

  return (
    <div>
      <Card>
        <h2>{department.name}</h2>
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          {!isPhdDepartment && (
          <TabPane tab={<span><LineChartOutlined />指标</span>} key="indicators">
            {indicatorsLoading ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <Spin size="large" />
              </div>
            ) : departmentIndicators ? (
              <DepartmentIndicatorsDisplay 
                indicators={departmentIndicators}
                department={department}
                targetRecords={targetRecords}
                selectedTargetYear={selectedTargetYear}
                onYearChange={setSelectedTargetYear}
                submissions={submissions}
                calculateCurrentValue={calculateCurrentValue}
                onIndicatorClick={(indicator) => {
                  const dataSource = getIndicatorDataSource(indicator.key);
                  if (dataSource) {
                    // Set initial selection for the statistics tab
                    setStatisticsInitialDataType(dataSource.data_type);
                    setStatisticsInitialFieldKey(dataSource.field_key);
                    setStatisticsInitialCategoryValue(dataSource.category_value || null);
                    // Navigate to the statistics tab
                    setActiveTab('statistics');
                  }
                }}
              />
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                <p>暂无指标数据</p>
              </div>
            )}
          </TabPane>
          )}
          {isSuperAdmin && (
            <TabPane tab={<span><CheckCircleOutlined />数据审核</span>} key="review">
                <div>
                <Tabs
                  activeKey={reviewStatusFilter}
                  onChange={(key) => {
                    setReviewStatusFilter(key);
                    setReviewBatchPage(1);
                    setReviewSelectedRowKeys([]); // Clear selection when switching tabs
                  }}
                  style={{ marginBottom: '16px' }}
                >
                  <TabPane tab="待审核" key="pending" />
                  <TabPane tab="已通过" key="approved" />
                  <TabPane tab="已拒绝" key="rejected" />
                  <TabPane tab="全部" key="all" />
                </Tabs>
                
                {renderReviewContent()}
              </div>
            </TabPane>
          )}
          {canInputData && (
          <TabPane tab={<span><PlusOutlined />数据录入</span>} key="input">
            <div style={{ marginBottom: '20px' }}>
              {isPhdDepartment ? (
                <PhdMatrixEntry
                  department={department}
                  submissions={submissions}
                  onRefresh={loadSubmissions}
                />
              ) : (
              <>
              {dataForms && dataForms.length > 0 ? (
                <Collapse
                  defaultActiveKey={[]}
                  style={{ background: '#fff' }}
                  items={dataForms.map(formConfig => {
                    // Get indicator targets for this form, show targets for the selected year
                    const formTargets = indicatorTargets.filter(t =>
                      t.data_type === formConfig.key && t.target_year === selectedTargetYear
                    );

                    return {
                      key: formConfig.key,
                      label: (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                          <span>{formConfig.label}</span>
                          {formTargets.length > 0 && (
                            <Tag color="blue" style={{ marginLeft: '8px' }}>
                              {formTargets.length} 个指标目标
                            </Tag>
                          )}
                        </div>
                      ),
                      children: (
                        <div>
                          {/* 显示指标进度 */}
                            <Card
                              type="inner"
                            title={
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span>指标进度</span>
                                <Select
                                  value={selectedTargetYear}
                                  onChange={setSelectedTargetYear}
                                  style={{ width: 120 }}
                                  size="small"
                                >
                                  {(() => {
                                    // Only show years that have targets for this data type
                                    const formTargetYears = indicatorTargets
                                      .filter(t => t.data_type === formConfig.key)
                                      .map(t => t.target_year)
                                      .filter(y => y !== null && y !== undefined);
                                    const availableYears = [...new Set(formTargetYears)].sort((a, b) => b - a);

                                    return availableYears.length > 0 ? (
                                      availableYears.map(year => (
                                        <Select.Option key={year} value={year}>
                                          {year}年
                                        </Select.Option>
                                      ))
                                    ) : (
                                      <Select.Option value={selectedTargetYear}>
                                        {selectedTargetYear}年
                                      </Select.Option>
                                    );
                                  })()}
                                </Select>
                              </div>
                            }
                              size="small"
                              style={{ marginBottom: '16px' }}
                            >
                            {formTargets.length > 0 ? (
                              <>
                              {formTargets.map(target => {
                                // Calculate current value (prefer data from target year)
                                let currentValue = calculateCurrentValue(
                                  formConfig.key,
                                  target.field_key,
                                  target.target_year,
                                  target.field_type,
                                  target.category_value
                                );

                                // If target year has no data, look for recent year data
                                let dataYear = target.target_year;
                                if (currentValue === null || currentValue === undefined) {
                                  const currentYear = new Date().getFullYear();
                                  // Search backwards from target year-1, up to 5 years
                                  for (let year = target.target_year - 1; year >= Math.max(currentYear - 5, 2000); year--) {
                                    const yearValue = calculateCurrentValue(
                                      formConfig.key,
                                      target.field_key,
                                      year,
                                      target.field_type,
                                      target.category_value
                                    );
                                    if (yearValue !== null && yearValue !== undefined) {
                                      currentValue = yearValue;
                                      dataYear = year;
                                      break;
                                    }
                                  }
                                }

                                const progress = currentValue !== null && currentValue !== undefined && target.target_value > 0
                                  ? Math.min((currentValue / target.target_value) * 100, 100)
                                  : 0;

                                // Display label (for category fields, show category value)
                                const displayLabel = target.field_type === 'category' && target.category_value
                                  ? `${target.field_label} - ${target.category_value}`
                                  : target.field_label;

                                // Format current value for display
                                let currentValueDisplay = '-';
                                if (currentValue !== null && currentValue !== undefined) {
                                  if (target.field_type === 'category') {
                                    currentValueDisplay = String(currentValue);
                                  } else {
                                    currentValueDisplay = typeof currentValue === 'number' ? currentValue.toFixed(4).replace(/\.?0+$/, '') : String(currentValue);
                                  }
                                  // If using data from a different year, annotate with year
                                  if (dataYear !== target.target_year) {
                                    currentValueDisplay += ` (${dataYear}年)`;
                                  }
                                }

                                return (
                                  <div key={target.id} style={{ marginBottom: '12px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', alignItems: 'center' }}>
                                      <span>{displayLabel}</span>
                                      <Space>
                                      <span style={{ color: '#666' }}>
                                        当前: {currentValueDisplay} / 目标: {typeof target.target_value === 'number' ? target.target_value.toFixed(4).replace(/\.?0+$/, '') : target.target_value} ({target.start_year && target.end_year ? `${target.start_year}-${target.end_year}` : target.target_year || '未设置'}年)
                                      </span>
                                      {canManageTargets ? (
                                        <Button
                                          type="link"
                                          size="small"
                                          icon={<EditOutlined />}
                                          onClick={() => {
                                            // Switch to target management tab
                                            setActiveTab('targets');
                                          }}
                                        >
                                          编辑
                                        </Button>
                                      ) : (
                                        <span style={{ color: '#999', fontSize: '12px' }}>
                                          （请联系管理员编辑目标）
                                        </span>
                                      )}
                                      </Space>
                                    </div>
                                    <Progress
                                      percent={progress}
                                      status={progress >= 100 ? 'success' : 'exception'}
                                      strokeColor={progress >= 100 ? undefined : '#ff4d4f'}
                                      format={() => `${progress.toFixed(1)}%`}
                                    />
                                  </div>
                                );
                              })}
                              </>
                            ) : (
                              <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                                <p>暂无目标设置，请联系管理员设置目标</p>
                              </div>
                            )}
                            </Card>
                          <EditableDataTable
                            dataType={formConfig.key}
                            submissions={submissions.filter(s => s.data_type === formConfig.key)}
                            fields={formConfig.fields}
                            onRefresh={loadSubmissions}
                            user={user}
                            departmentId={department.id}
                          />
                        </div>
                      )
                    };
                  })}
                />
              ) : (
                <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                  <p>该部门暂无数据录入表单</p>
                </div>
              )}
              </>
              )}
            </div>
          </TabPane>
          )}
          {canManageTargets && (
            <TabPane tab={<span><AimOutlined />目标管理</span>} key="targets">
              {isPhdDepartment ? (
                <PhdTargetMatrix
                  department={department}
                  targetRecords={targetRecords}
                  onRefresh={loadIndicatorTargets}
                />
              ) : (
                <EditableTargetTable
                  department={department}
                  targetRecords={targetRecords}
                  onRefresh={loadIndicatorTargets}
                  dataForms={dataForms}
                />
              )}
            </TabPane>
          )}
          <TabPane tab={<span><DownloadOutlined />数据导出</span>} key="export">
            {dataForms && dataForms.length > 0 ? (
              <>
                {isPhdDepartment && (
                  <PhdExportMatrixPreview
                    dataForms={dataForms}
                    submissions={submissions}
                    departmentId={department.id}
                    targetRecords={targetRecords}
                  />
                )}
              {!isPhdDepartment && (
              <Collapse
                defaultActiveKey={[]}
                style={{ background: '#fff' }}
                items={dataForms.map(formConfig => {
                  // Get all submissions for this form type (regardless of review status)
                  const allSubmissions = submissions.filter(s => 
                    s.data_type === formConfig.key
                  );
                  
                  // Filter: keep only the latest record per year (excluding overwritten records)
                  // For year-based data types, keep only the latest record per year
                  // For list-type data types, return all records
                  const latestSubmissions = filterLatestByYear(allSubmissions, formConfig.key);
                  
                  return {
                    key: formConfig.key,
                    label: (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                        <span>{formConfig.label}</span>
                        <Tag color="green" style={{ marginLeft: '8px' }}>
                          {latestSubmissions.length} 条数据{yearBasedDataTypes.includes(formConfig.key) ? '（已过滤被覆盖的记录）' : ''}
                        </Tag>
                      </div>
                    ),
                    children: (
                      <div>
                        <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'flex-end' }}>
                          <Button
                            type="primary"
                            icon={<DownloadOutlined />}
                            onClick={async () => {
                              if (latestSubmissions.length === 0) {
                                message.warning('没有数据可导出');
                                return;
                              }

                              try {
                                const params = new URLSearchParams();
                                params.append('department_id', department.id);
                                params.append('data_type', formConfig.key);
                                params.append('status', 'approved');
                                if (isPhdDepartment) {
                                  params.append('transpose_phd', '1');
                                  params.append('include_targets', 'true');
                                }

                                const token = getToken();
                                if (!token) {
                                  message.error('请先登录');
                                  return;
                                }

                                // Use the same API URL function from api.js
                                // This ensures consistency and avoids code duplication
                                const apiUrl = getApiBaseURL();
                                const response = await fetch(`${apiUrl}/submissions/export?${params.toString()}`, {
                                  method: 'GET',
                                  headers: {
                                    'Authorization': `Bearer ${token}`
                                  }
                                });

                                if (!response.ok) {
                                  let errorMessage = '导出失败';
                                try {
                                  const error = await response.json();
                                  errorMessage = error.error || errorMessage;
                                } catch (e) {
                                  errorMessage = response.statusText || errorMessage;
                                }
                                
                                if (response.status === 401 || response.status === 422) {
                                  errorMessage = '登录已过期，请重新登录';
                                }
                                
                                throw new Error(errorMessage);
                              }

                              // Get filename
                              const contentDisposition = response.headers.get('Content-Disposition');
                              let filename = `${formConfig.label}_${new Date().getTime()}.xlsx`;
                              if (contentDisposition) {
                                const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                                if (filenameMatch && filenameMatch[1]) {
                                  filename = filenameMatch[1].replace(/['"]/g, '');
                                  if (filename.startsWith('UTF-8\'\'')) {
                                    filename = decodeURIComponent(filename.replace('UTF-8\'\'', ''));
                                  }
                                }
                              }

                              // Create Blob and download
                              const blob = await response.blob();
                              const url = window.URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = filename;
                              document.body.appendChild(a);
                              a.click();
                              window.URL.revokeObjectURL(url);
                              document.body.removeChild(a);

                              message.success('导出成功');
                            } catch (error) {
                              console.error('导出失败:', error);
                              message.error(error.message || '导出失败');
                            }
                          }}
                          disabled={latestSubmissions.length === 0}
                        >
                          导出Excel
                        </Button>
                      </div>
                      {latestSubmissions.length === 0 ? (
                        <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
                          <p>暂无已审核的数据</p>
                        </div>
                      ) : isPhdDepartment ? (
                        <p style={{ color: '#888', fontSize: 13, marginTop: 8 }}>
                          页面顶部总表已汇总各指标；此处导出为<strong>仅本指标</strong>的矩阵 Excel（年份列与总表规则相同：仅含该指标有数据的年份）。
                        </p>
                      ) : (
                        <DataTable
                          dataType={formConfig.key}
                          submissions={latestSubmissions}
                          fields={formConfig.fields}
                          onRefresh={loadSubmissions}
                          user={user}
                          departmentId={department.id}
                          disableBatchGrouping={true}
                        />
                      )}
                    </div>
                    )
                  };
                })}
              />
              )}
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                <p>该部门暂无数据表单</p>
              </div>
            )}
          </TabPane>
          {/* 历史记录标签暂时移除 */}
          {/* 博士授权点体系内的部门不显示“数据统计”标签页，只保留数据录入和指标 */}
          {!isPhdDepartment && (
            <TabPane tab={<span><BarChartOutlined />数据统计</span>} key="statistics">
              <DataChart
                department={department}
                dataForms={dataForms}
                submissions={submissions}
                initialDataType={statisticsInitialDataType}
                initialFieldKey={statisticsInitialFieldKey}
                initialCategoryValue={statisticsInitialCategoryValue}
              />
            </TabPane>
          )}
        </Tabs>
      </Card>

      <Modal
        title={currentForm?.label}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={800}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          onValuesChange={(changedValues, allValues) => {
            // When form values change, update computed fields
            if (currentForm) {
              currentForm.fields.forEach(f => {
                if (f.computed && f.formula) {
                  const computedValue = f.formula(allValues);
                  form.setFieldsValue({ [f.key]: computedValue });
                }
              });
              
              // Check if all select type field values are still valid
              currentForm.fields.forEach(f => {
                if (f.type === 'select' && f.options) {
                  const currentValue = allValues[f.key];
                  if (currentValue !== undefined && currentValue !== null) {
                    // Check if current value is in the options list
                    const isValid = f.options.some(opt => opt.value === currentValue);
                    if (!isValid) {
                      // If value is not in options list, clear the field value
                      form.setFieldsValue({ [f.key]: undefined });
                    }
                  }
                }
              });
            }
          }}
        >
          {currentForm?.fields.map(field => {
            if (field.computed) {
              return (
                <Form.Item key={field.key} label={field.label}>
                  <Form.Item noStyle shouldUpdate>
                    {({ getFieldsValue }) => {
                      const values = getFieldsValue();
                      const computedValue = field.formula ? field.formula(values) : '';
                      return <Input disabled value={computedValue || ''} />;
                    }}
                  </Form.Item>
                </Form.Item>
              );
            }

            let inputComponent;
            switch (field.type) {
              case 'number':
                // Determine if special configuration is needed based on field name and label
                const isYear = field.key === 'year' || field.key === '年份' || field.label.includes('年份') || field.label.includes('年度');
                const isMoney = field.label.includes('万元') || field.label.includes('经费') || field.label.includes('收入');
                const isCount = field.label.includes('人数') || field.label.includes('数量') || field.label.includes('台') || field.label.includes('种');
                
                if (isYear) {
                  // Year selector: use year selection, range from 2000 to current year+5
                  const currentYear = new Date().getFullYear();
                  const yearOptions = [];
                  for (let y = 2000; y <= currentYear + 5; y++) {
                    yearOptions.push(y);
                  }
                  inputComponent = (
                    <Select
                      style={{ width: '100%' }}
                      placeholder="选择年份"
                      showSearch
                      filterOption={(input, option) =>
                        option.children.toString().includes(input)
                      }
                    >
                      {yearOptions.map(year => (
                        <Select.Option key={year} value={year}>{year}</Select.Option>
                      ))}
                    </Select>
                  );
                } else {
                  // Number input: configure differently based on type
                  inputComponent = (
                    <InputNumber
                      style={{ width: '100%' }}
                      min={0}
                      precision={isMoney ? 2 : 0} // Money keeps 2 decimal places, others are integers
                      formatter={isMoney ? value => {
                        if (!value && value !== 0) return '';
                        return `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                      } : undefined}
                      parser={isMoney ? value => {
                        if (!value) return '';
                        return value.replace(/\$\s?|(,*)/g, '');
                      } : undefined}
                      placeholder={isMoney ? '请输入金额（万元）' : isCount ? '请输入数量' : '请输入数字'}
                      controls={true}
                      // Prevent non-numeric character input
                      onKeyDown={(e) => {
                        // Allow: digits, decimal point, backspace, delete, Tab, Enter, arrow keys, Home, End
                        const allowedKeys = [
                          'Backspace', 'Delete', 'Tab', 'Enter', 'Escape',
                          'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
                          'Home', 'End', 'Control', 'Meta', 'Alt'
                        ];
                        const isNumber = /^[0-9]$/.test(e.key);
                        const isDecimal = e.key === '.' && isMoney;
                        const isAllowedKey = allowedKeys.includes(e.key) || e.ctrlKey || e.metaKey || e.altKey;
                        
                        // If not a digit, decimal point (when allowed), or allowed key, prevent input
                        if (!isNumber && !isDecimal && !isAllowedKey) {
                          e.preventDefault();
                        }
                      }}
                      // Handle paste, only allow digits and decimal point
                      onPaste={(e) => {
                        e.preventDefault();
                        const paste = (e.clipboardData || window.clipboardData).getData('text');
                        // Extract digits and decimal point
                        let numericValue = paste.replace(/[^\d.]/g, '');
                        // If multiple decimal points, keep only the first
                        const parts = numericValue.split('.');
                        if (parts.length > 2) {
                          numericValue = parts[0] + '.' + parts.slice(1).join('');
                        }
                        if (numericValue) {
                          const num = parseFloat(numericValue);
                          if (!isNaN(num) && num >= 0) {
                            if (!isMoney && numericValue.includes('.')) {
                              // Integer type does not allow decimal points, round down
                              form.setFieldsValue({ [field.key]: Math.floor(num) });
                            } else {
                              form.setFieldsValue({ [field.key]: num });
                            }
                          }
                        }
                      }}
                      // Restrict input value changes, ensure only valid numbers are accepted
                      onChange={(value) => {
                        // InputNumber handles this automatically, additional validation can be added here
                        if (value !== null && value !== undefined) {
                          if (!isMoney && value % 1 !== 0) {
                            // Integer type, auto-round down
                            form.setFieldsValue({ [field.key]: Math.floor(value) });
                          }
                        }
                      }}
                    />
                  );
                }
                break;
              case 'date':
                // Date input: supports scroll wheel selection and numeric input
                inputComponent = (
                  <DateInput
                    style={{ width: '100%' }}
                    placeholder="YYYY-MM-DD（可输入数字或滚轮调整）"
                  />
                );
                break;
              case 'daterange':
                // Date range input: supports scroll wheel selection and numeric input
                inputComponent = (
                  <DateRangeInput
                    style={{ width: '100%' }}
                    placeholder="YYYY-MM-DD ~ YYYY-MM-DD（可输入数字或滚轮调整）"
                  />
                );
                break;
              case 'textarea':
                inputComponent = (
                  <Input.TextArea 
                    rows={4} 
                    maxLength={field.maxLength} 
                    showCount={field.maxLength ? true : false}
                    placeholder={`请输入${field.label}${field.maxLength ? `（最多${field.maxLength}字）` : ''}`}
                  />
                );
                break;
              case 'select':
                // Dropdown select: if field has option configuration
                inputComponent = (
                  <Select
                    style={{ width: '100%' }}
                    placeholder={`请选择${field.label}`}
                    showSearch={field.options && field.options.length > 5}
                    filterOption={(input, option) =>
                      option.children.toLowerCase().includes(input.toLowerCase())
                    }
                  >
                    {field.options?.map(option => (
                      <Select.Option key={option.value} value={option.value}>
                        {option.label}
                      </Select.Option>
                    ))}
                  </Select>
                );
                break;
              default:
                // Text input: add input restrictions based on field type
                const isText = field.type === 'text' || !field.type;
                inputComponent = (
                  <Input 
                    placeholder={`请输入${field.label}`}
                    maxLength={field.maxLength}
                    // For certain specific fields, input restrictions can be added
                    onKeyPress={field.key === 'name' || field.key === '名称' || field.key === 'author' || field.key === '作者' || field.key === 'leader_name' || field.key === '带头人姓名' || field.key === 'director' || field.key === '负责人' || field.key === 'winner' || field.key === '获奖人' 
                      ? (e) => {
                          // Name fields can accept Chinese, English, numbers, and common punctuation
                          const char = String.fromCharCode(e.which || e.keyCode);
                          // Allow most characters, but specific restrictions can be added here
                        }
                      : undefined
                    }
                  />
                );
            }

            return (
              <Form.Item
                key={field.key}
                name={field.key}
                label={field.label}
                rules={[{ required: field.required, message: `请输入${field.label}` }]}
              >
                {inputComponent}
              </Form.Item>
            );
          })}
          {/* 添加提交时间字段 - 使用逗号分隔的数值选择方式（只有年月日） */}
          <Form.Item label="提交时间" required>
            <Space.Compact style={{ width: '100%' }}>
              {/* 年份选择 */}
              <Form.Item
                name="submitted_year"
                rules={[{ required: true, message: '年' }]}
                initialValue={getCurrentDateParts().submitted_year}
                style={{ marginBottom: 0, width: '33%' }}
              >
                <Select placeholder="年" showSearch>
                  {(() => {
                    const currentYear = new Date().getFullYear();
                    const years = [];
                    for (let y = 2000; y <= currentYear + 5; y++) {
                      years.push(
                        <Select.Option key={y} value={y}>{y}</Select.Option>
                      );
                    }
                    return years;
                  })()}
                </Select>
              </Form.Item>
              
              {/* 月份选择 */}
              <Form.Item
                name="submitted_month"
                rules={[{ required: true, message: '月' }]}
                initialValue={getCurrentDateParts().submitted_month}
                style={{ marginBottom: 0, width: '33%' }}
              >
                <Select placeholder="月" showSearch>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                    <Select.Option key={month} value={month}>
                      {String(month).padStart(2, '0')}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              
              {/* 日期选择 */}
              <Form.Item
                name="submitted_day"
                rules={[{ required: true, message: '日' }]}
                initialValue={getCurrentDateParts().submitted_day}
                style={{ marginBottom: 0, width: '34%' }}
                dependencies={['submitted_year', 'submitted_month']}
              >
                <Form.Item shouldUpdate={(prevValues, currentValues) => 
                  prevValues.submitted_year !== currentValues.submitted_year ||
                  prevValues.submitted_month !== currentValues.submitted_month
                }>
                  {({ getFieldValue }) => {
                    const year = getFieldValue('submitted_year');
                    const month = getFieldValue('submitted_month');
                    const daysInMonth = year && month ? moment(`${year}-${month}`, 'YYYY-MM').daysInMonth() : 31;
                    
                    return (
                      <Select placeholder="日" showSearch>
                        {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => (
                          <Select.Option key={day} value={day}>
                            {String(day).padStart(2, '0')}
                          </Select.Option>
                        ))}
                      </Select>
                    );
                  }}
                </Form.Item>
              </Form.Item>
            </Space.Compact>
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">提交</Button>
              <Button onClick={() => setModalVisible(false)}>取消</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>


      <RejectModal
        visible={rejectModalVisible}
        onCancel={() => {
          setRejectModalVisible(false);
          setRejectTargetIds([]);
          setRejectTargetSubmissions([]);
        }}
        onOk={handleRejectSubmit}
        confirmLoading={rejectSubmitting}
        form={rejectForm}
      >
        {rejectTargetSubmissions.length > 0 && (
          <Form.Item label={`选中的数据行（共 ${rejectTargetSubmissions.length} 条）`}>
            <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #E1E5EB', borderRadius: '4px' }}>
              <DataTable
                dataType={rejectTargetSubmissions[0]?.data_type}
                submissions={rejectTargetSubmissions}
                fields={dataForms.find(f => f.key === rejectTargetSubmissions[0]?.data_type)?.fields || []}
                onRefresh={() => {}}
                user={user}
                departmentId={department?.id}
                disableBatchGrouping={true}
              />
            </div>
          </Form.Item>
        )}
      </RejectModal>
    </div>
  );
};

// Department indicator chart component
const IndicatorChart = ({ departmentIds, indicator, indicatorTargets, currentValue, submissions, calculateCurrentValue, getIndicatorDataSource }) => {
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (departmentIds && departmentIds.length > 0 && indicator) {
      loadIndicatorChart();
    }
  }, [departmentIds, indicator, submissions, calculateCurrentValue, getIndicatorDataSource]);
  
  const loadIndicatorChart = async () => {
    setLoading(true);
    try {
      // First try using the backend API
      const deptIdsParam = departmentIds.map(id => `dept_ids=${id}`).join('&');
      const response = await api.get(`/indicators/chart?indicator_key=${indicator.key}&${deptIdsParam}`);
      
      const data = response.data || {};
      const labels = data.labels || [];
      const values = data.values || [];
      
      // If backend API returned valid data, use backend data
      if (labels.length > 0 && values.length > 0) {
        const chartLabels = labels;
        const chartValues = labels.map((label, index) => {
          const value = values[index];
          return value !== null && value !== undefined && !isNaN(value) ? value : 0;
        });
        
        setChartData({
          labels: chartLabels,
          values: chartValues
        });
        setLoading(false);
        return;
      }
      
      // If backend API returned no data, calculate from frontend data submission
      if (submissions && calculateCurrentValue && getIndicatorDataSource) {
        const dataSource = getIndicatorDataSource(indicator.key);
        if (dataSource) {
          // Get all years that have data (excluding invalid years)
          // Extract year from submissions, prefer s.year, if null extract from data_content
          const availableYearsSet = new Set();
          submissions
            .filter(s => s.data_type === dataSource.data_type && s.status === 'approved')
            .forEach(s => {
              let year = s.year;
              // If year field is invalid, extract from data_content
              if (!year || year === null || year === undefined || year <= 0) {
                try {
                  const content = typeof s.data_content === 'string' ? JSON.parse(s.data_content) : (s.data_content || {});
                  year = content.year || content['年份'] || content['获奖年度'];
                  if (year && typeof year === 'string') {
                    // Try to extract 4-digit year from string
                    const yearMatch = year.match(/\d{4}/);
                    if (yearMatch) {
                      year = parseInt(yearMatch[0], 10);
                    } else {
                      year = parseInt(year, 10);
                    }
                  }
                } catch (e) {
                  // Parse failed, skip
                  return;
                }
              }
              if (year && !isNaN(year) && year > 0 && year >= 1900 && year <= 2100) {
                availableYearsSet.add(year);
              }
            });
          const availableYears = Array.from(availableYearsSet).sort((a, b) => a - b);
          
          if (availableYears.length > 0) {
            const chartLabels = availableYears.map(y => `${y}年`);
            const chartValues = availableYears.map(year => {
              const value = calculateCurrentValue(
                dataSource.data_type,
                dataSource.field_key,
                year,
                dataSource.field_type,
                dataSource.category_value
              );
              return value !== null && value !== undefined ? value : 0;
            });
            
            setChartData({
              labels: chartLabels,
              values: chartValues
            });
            setLoading(false);
            return;
          }
        }
      }
      
      // If no data available, set to null
      setChartData(null);
    } catch (error) {
      console.error('加载指标图表失败:', error);
      
      // When API fails, try to calculate from frontend data submission
      if (submissions && calculateCurrentValue && getIndicatorDataSource) {
        const dataSource = getIndicatorDataSource(indicator.key);
        if (dataSource) {
          // Get all years that have data (excluding invalid years)
          // Extract year from submissions, prefer s.year, if null extract from data_content
          const availableYearsSet = new Set();
          submissions
            .filter(s => s.data_type === dataSource.data_type && s.status === 'approved')
            .forEach(s => {
              let year = s.year;
              // If year field is invalid, extract from data_content
              if (!year || year === null || year === undefined || year <= 0) {
                try {
                  const content = typeof s.data_content === 'string' ? JSON.parse(s.data_content) : (s.data_content || {});
                  year = content.year || content['年份'] || content['获奖年度'];
                  if (year && typeof year === 'string') {
                    // Try to extract 4-digit year from string
                    const yearMatch = year.match(/\d{4}/);
                    if (yearMatch) {
                      year = parseInt(yearMatch[0], 10);
                    } else {
                      year = parseInt(year, 10);
                    }
                  }
                } catch (e) {
                  // Parse failed, skip
                  return;
                }
              }
              if (year && !isNaN(year) && year > 0 && year >= 1900 && year <= 2100) {
                availableYearsSet.add(year);
              }
            });
          const availableYears = Array.from(availableYearsSet).sort((a, b) => a - b);
          
          if (availableYears.length > 0) {
            const chartLabels = availableYears.map(y => `${y}年`);
            const chartValues = availableYears.map(year => {
              const value = calculateCurrentValue(
                dataSource.data_type,
                dataSource.field_key,
                year,
                dataSource.field_type,
                dataSource.category_value
              );
              return value !== null && value !== undefined ? value : 0;
            });
            
            setChartData({
              labels: chartLabels,
              values: chartValues
            });
            setLoading(false);
            return;
          }
        }
      }
      
      setChartData(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <IndicatorChartBase
      chartData={chartData}
      indicator={indicator}
      indicatorTargets={indicatorTargets}
      loading={loading}
    />
  );
};

// Department indicator display component
const DepartmentIndicatorsDisplay = ({ indicators, department, targetRecords, selectedTargetYear, onYearChange, submissions, calculateCurrentValue, onIndicatorClick }) => {
  const [chartVisible, setChartVisible] = useState(false);
  const [selectedIndicator, setSelectedIndicator] = useState(null);
  const [indicatorTargets, setIndicatorTargets] = useState({});
  
  // Load indicator target data (for chart display)
  useEffect(() => {
    if (department && department.id) {
      loadIndicatorTargets();
    }
  }, [department]);
  
  const loadIndicatorTargets = async () => {
    if (!department || !department.id) return;
    try {
      const response = await api.get('/indicator-targets', {
        params: { department_id: department.id }
      });
      const targets = response.data || [];
      
      // Group by indicator key, each indicator can have multiple targets
      // Need to map field_key back to indicator key
      const targetsByIndicator = {};
      
      // First create a reverse mapping from field_key to indicator key
      const fieldKeyToIndicatorKey = {};
      const deptIndicators = getDepartmentIndicators(department?.name || '');
      deptIndicators.forEach(ind => {
        const dataSource = getIndicatorDataSource(ind.key);
        if (dataSource) {
          // For category fields, need to include category_value
          if (dataSource.field_type === 'category' && dataSource.category_value) {
            const key = `${dataSource.field_key}_${dataSource.category_value}`;
            fieldKeyToIndicatorKey[key] = ind.key;
          } else if (dataSource.field_type === 'number' || dataSource.field_type === 'count') {
            // For numeric fields and count type, use field_key directly
            fieldKeyToIndicatorKey[dataSource.field_key] = ind.key;
          }
        }
      });
      
      targets.forEach(target => {
        // Try to find the corresponding indicator key
        let indicatorKey = null;
        
        // For category fields, need to match category_value
        if (target.field_type === 'category' && target.category_value) {
          const key = `${target.field_key}_${target.category_value}`;
          indicatorKey = fieldKeyToIndicatorKey[key];
        } else if (target.field_type === 'number' || !target.field_type) {
          // For numeric fields, match field_key directly
          indicatorKey = fieldKeyToIndicatorKey[target.field_key];
        }
        
        // If no matching indicator key found, try matching by data_type and field_key
        if (!indicatorKey) {
          // Loop through all department indicators to find a match
          deptIndicators.forEach(ind => {
            const dataSource = getIndicatorDataSource(ind.key);
            if (dataSource) {
              // Check if it matches
              if (dataSource.data_type === target.data_type && 
                  dataSource.field_key === target.field_key) {
                // For category fields, also need to match category_value
                if (dataSource.field_type === 'category' && dataSource.category_value) {
                  if (target.category_value === dataSource.category_value) {
                    indicatorKey = ind.key;
                  }
                } else if (dataSource.field_type === 'number' || dataSource.field_type === 'count') {
                  // For numeric fields, data_type and field_key matching is sufficient
                  if (!target.category_value || target.category_value === null) {
                    indicatorKey = ind.key;
                  }
                }
              }
            }
          });
        }
        
        // If still no matching indicator key, use field_key as key (backward compatibility)
        const key = indicatorKey || target.field_key;
        
        if (!targetsByIndicator[key]) {
          targetsByIndicator[key] = [];
        }
        targetsByIndicator[key].push({
          id: target.id,
          target: target.target_value,
          targetYear: target.target_year,
          startYear: target.start_year,
          endYear: target.end_year,
          fieldLabel: target.field_label
        });
      });
      
      // Sort targets for each indicator by year
      Object.keys(targetsByIndicator).forEach(key => {
        targetsByIndicator[key].sort((a, b) => a.targetYear - b.targetYear);
      });
      
      setIndicatorTargets(targetsByIndicator);
    } catch (error) {
      console.error('加载指标目标失败:', error);
      setIndicatorTargets({});
    }
  };
  
  const handleIndicatorClick = (indicator) => {
    // If onIndicatorClick callback is provided, use it (navigate to statistics tab)
    if (onIndicatorClick) {
      onIndicatorClick(indicator);
    } else {
      // Otherwise use the original Modal approach
    setSelectedIndicator(indicator);
    setChartVisible(true);
    }
  };
  
  // Map indicator key to data source (data_type, field_key, field_type, category_value)
  const getIndicatorDataSource = (indicatorKey) => {
    const dataSourceMap = {
      'fulltime_teachers_count': { data_type: 'teacher_count', field_key: '专任教师总人数', field_type: 'number' },
      'senior_title_count': { data_type: 'teacher_count', field_key: '专任教师正高数量', field_type: 'number' },
      'associate_title_count': { data_type: 'teacher_count', field_key: '专任教师副高数量', field_type: 'number' },
      'undergraduate_count': { data_type: 'student_count', field_key: '本科人数', field_type: 'number' },
      'college_count': { data_type: 'student_count', field_key: '专科人数', field_type: 'number' },
      'research_awards': { data_type: 'research_awards', field_key: '奖励类别', field_type: 'category', category_value: '国家级奖励' },
      'provincial_awards': { data_type: 'research_awards', field_key: '奖励类别', field_type: 'category', category_value: '省部级奖励' },
      'industry_awards': { data_type: 'research_awards', field_key: '奖励类别', field_type: 'category', category_value: '行业奖励' },
      'national_projects': { data_type: 'research_projects', field_key: '类别', field_type: 'category', category_value: '国家级项目' },
      'provincial_projects': { data_type: 'research_projects', field_key: '类别', field_type: 'category', category_value: '省部级项目' },
      'national_research_platforms': { data_type: 'research_platforms', field_key: '批准部门', field_type: 'category', category_value: '国家级' },
      'provincial_research_platforms': { data_type: 'research_platforms', field_key: '批准部门', field_type: 'category', category_value: '省部级' },
      'national_teaching_awards': { data_type: 'teaching_awards', field_key: '奖励类别', field_type: 'category', category_value: '国家级教学成果奖' },
      'provincial_teaching_awards': { data_type: 'teaching_awards', field_key: '奖励类别', field_type: 'category', category_value: '省级教学成果奖' },
      'national_teaching_platforms': { data_type: 'teaching_platforms', field_key: '类别', field_type: 'category', category_value: '国家级' },
      'provincial_teaching_platforms': { data_type: 'teaching_platforms', field_key: '类别', field_type: 'category', category_value: '省部级' },
      'phd_supervisors': { data_type: 'degree_points', field_key: '授权级别类型', field_type: 'category', category_value: '博士' },
      'phd_graduates': { data_type: 'graduate_students', field_key: '博士研究生', field_type: 'number' },
      'master_students': { data_type: 'graduate_students', field_key: '硕士研究生', field_type: 'number' },
      'graduate_platforms': { data_type: 'graduate_platforms', field_key: '名称', field_type: 'count' }, // Count number of records (special handling)
      'international_students_count': { data_type: 'international_students', field_key: '留学生人数', field_type: 'number' },
      'cooperation_institutions': { data_type: 'international_exchange', field_key: '中外合作办学机构数', field_type: 'number' },
      'cooperation_projects': { data_type: 'international_exchange', field_key: '中外合作办学项目数', field_type: 'number' },
      'annual_funding': { data_type: 'research_funding', field_key: '经费情况(万元)', field_type: 'number' },
    };
    return dataSourceMap[indicatorKey];
  };
  
  // Determine which indicators should be shown based on department name
  const getDepartmentIndicators = (deptName) => {
    const deptIndicatorMap = {
      '人事处': [
        { key: 'fulltime_teachers_count', label: '专任教师人数', unit: '人', group: '师资团队' },
        { key: 'senior_title_count', label: '专任教师正高数量', unit: '人', group: '师资团队' },
        { key: 'associate_title_count', label: '专任教师副高数量', unit: '人', group: '师资团队' }
      ],
      '科研处': [
        { key: 'research_awards', label: '国家级奖励', unit: '项', group: '科学研究' },  // Corresponds to national_awards
        { key: 'provincial_awards', label: '省部级奖励', unit: '项', group: '科学研究' },
        { key: 'industry_awards', label: '行业奖励', unit: '项', group: '科学研究' },  // Corresponds to association_awards
        { key: 'national_projects', label: '国家级项目', unit: '项', group: '科学研究' },
        { key: 'provincial_projects', label: '省部级项目', unit: '项', group: '科学研究' },
        { key: 'annual_funding', label: '年到帐经费', unit: '万元', group: '科学研究' },
        { key: 'national_research_platforms', label: '国家级科研平台', unit: '个', group: '支撑平台' },
        { key: 'provincial_research_platforms', label: '省部级科研平台', unit: '个', group: '支撑平台' }
      ],
      '教务处': [
        { key: 'national_teaching_awards', label: '国家级教学成果', unit: '项', group: '人才培养' },
        { key: 'provincial_teaching_awards', label: '省部级教学成果', unit: '项', group: '人才培养' },
        { key: 'undergraduate_count', label: '本科生人数', unit: '人', group: '人才培养' },
        { key: 'college_count', label: '专科生人数', unit: '人', group: '人才培养' },
        { key: 'national_teaching_platforms', label: '国家级教学平台', unit: '个', group: '支撑平台' },
        { key: 'provincial_teaching_platforms', label: '省部级教学平台', unit: '个', group: '支撑平台' }
      ],
      '研究生院(研究生工作部)': [
        { key: 'phd_supervisors', label: '博导', unit: '人', group: '师资团队' },
        { key: 'phd_graduates', label: '培养博士人数', unit: '人', group: '人才培养' },
        { key: 'master_students', label: '硕士生人数', unit: '人', group: '人才培养' },
        { key: 'graduate_platforms', label: '研究生院平台', unit: '个', group: '支撑平台' }
      ],
      '国际事务处': [
        { key: 'international_students_count', label: '留学生人数', unit: '人', group: '国际交流' },
        { key: 'cooperation_institutions', label: '合作机构数', unit: '个', group: '国际交流' },
        { key: 'cooperation_projects', label: '合作项目数', unit: '项', group: '国际交流' }
      ],
    };

    return deptIndicatorMap[deptName] || [];
  };

  // Get the indicators to display for this department
  const deptIndicators = getDepartmentIndicators(department?.name || '');
  
  // Organize indicators by group
  const indicatorGroups = {};
  deptIndicators.forEach(ind => {
    if (!indicatorGroups[ind.group]) {
      indicatorGroups[ind.group] = [];
    }
    indicatorGroups[ind.group].push(ind);
  });

  // Convert to array format
  const indicatorGroupsArray = Object.keys(indicatorGroups).map(title => ({
    title,
    indicators: indicatorGroups[title]
  }));

  // Generate year options: show all years (2000 to current year)
  const availableYears = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years = [];
    // From 2000 to current year
    for (let year = 2000; year <= currentYear; year++) {
      years.push(year);
    }
    return years.sort((a, b) => b - a);
  }, []);

  // Mapping from indicator key to field_key (for target matching)
  const getFieldKeyFromIndicatorKey = (indicatorKey) => {
    const dataSource = getIndicatorDataSource(indicatorKey);
    return dataSource ? dataSource.field_key : indicatorKey;
  };

  // Get currently active target from target records (matched by indicator key)
  const getActiveTarget = (indicatorKey) => {
    if (!targetRecords || targetRecords.length === 0) return null;
    
    // Get data source info corresponding to the indicator
    const dataSource = getIndicatorDataSource(indicatorKey);
    if (!dataSource) return null;
    
    // Match target records based on data source info
    let indicatorTargets = [];
    
    // For category fields, need to match data_type, field_key, and category_value
    if (dataSource.field_type === 'category' && dataSource.category_value) {
      indicatorTargets = targetRecords.filter(t => 
        t.data_type === dataSource.data_type &&
        t.field_key === dataSource.field_key &&
        t.category_value === dataSource.category_value
      );
    } else {
      // For numeric fields, match data_type and field_key
      indicatorTargets = targetRecords.filter(t => 
        t.data_type === dataSource.data_type &&
        t.field_key === dataSource.field_key &&
        (!t.category_value || t.category_value === null)
      );
    }
    
    if (indicatorTargets.length === 0) return null;
    
    // Show target for the specified year
    const yearTarget = indicatorTargets.find(t => t.target_year === selectedTargetYear);
    
    if (yearTarget) {
      return {
        target: yearTarget.target_value,
        targetYear: yearTarget.target_year
      };
    }
    
    // If no target for specified year, return null (hide progress)
    return null;
  };

  if (indicatorGroupsArray.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
        <p>该部门暂无相关指标数据</p>
      </div>
    );
  }

  return (
    <div>
      {/* 年份选择器 */}
      <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
        <span style={{ marginRight: '8px' }}>选择年份：</span>
        <Select
          value={selectedTargetYear}
          onChange={onYearChange}
          style={{ width: 120 }}
        >
          {availableYears.map(year => (
            <Select.Option key={year} value={year}>
              {year}年
            </Select.Option>
          ))}
        </Select>
      </div>
      
      {indicatorGroupsArray.map((group, groupIndex) => {
        // Do not filter indicators, show all defined indicators (null values displayed as '-')
        const groupIndicators = group.indicators;

        if (groupIndicators.length === 0) return null;

        return (
          <Card
            key={groupIndex}
            type="inner"
            title={group.title}
            style={{ marginBottom: '16px' }}
          >
            <Row gutter={[16, 16]}>
              {groupIndicators.map((indicator) => {
                // For year-based indicators (e.g., teaching awards, research awards), prefer selected year data
                // For sum-based indicators (e.g., HR senior title count), use backend API sum values
                let value = null;
                // All indicators display by year
                // Explicitly do not use backend API indicator values (these are sums)
                // All indicators calculated from frontend submissions by year
                if (calculateCurrentValue && submissions) {
                  const dataSource = getIndicatorDataSource(indicator.key);
                  if (dataSource) {
                    // Use the selected year
                    value = calculateCurrentValue(
                      dataSource.data_type,
                      dataSource.field_key,
                      selectedTargetYear,
                      dataSource.field_type,
                      dataSource.category_value
                    );
                    
                    // For PhD supervisor indicator, multiply PhD program count by 2 (average 2 supervisors per program)
                    if (indicator.key === 'phd_supervisors' && value !== null && value !== undefined) {
                      value = value * 2;
                    }
                    
                    // Note: no longer fall back to other years, only show selected year data
                    // If selected year has no data, value stays null, displayed as '-'
                  }
                }
                // Do not use backend API sum values, ensure selected year data is displayed
                
                // Format display value: decide decimal places based on indicator type
                // For integer indicators (people count, quantity), show as integer; for funding, show 2 decimal places
                const formatDisplayValue = (val) => {
                  if (val === null || val === undefined) return '-';
                  if (typeof val !== 'number') return val;
                  
                  // Determine if indicator is an integer type (people, quantity, items, etc.)
                  const isIntegerType = indicator.unit === '人' || indicator.unit === '项' || indicator.unit === '个';
                  
                  if (isIntegerType) {
                    // Integer type: if integer, display directly; if decimal, show 2 decimal places
                    return Number.isInteger(val) ? val.toString() : val.toFixed(2);
                  } else {
                    // Other types (e.g., ten-thousands yuan): show 2 decimal places
                    return val.toFixed(2);
                  }
                };
                
                const displayValue = formatDisplayValue(value);
                
                // Calculate progress (using targets loaded from backend API)
                const activeTarget = getActiveTarget(indicator.key);
                let progress = null;
                let progressPercent = 0;
                if (activeTarget && value !== null && value !== undefined && typeof value === 'number') {
                  progressPercent = Math.min((value / activeTarget.target) * 100, 100);
                  progress = {
                    current: value,
                    target: activeTarget.target,
                    percent: progressPercent,
                    targetYear: activeTarget.targetYear,
                    startYear: activeTarget.startYear,
                    endYear: activeTarget.endYear
                  };
                }
                
                return (
                  <Col xs={24} sm={12} md={8} lg={6} key={indicator.key}>
                    <Card
                      hoverable
                      style={{ 
                        cursor: 'pointer',
                        textAlign: 'center',
                        height: '100%',
                        position: 'relative'
                      }}
                      onClick={() => handleIndicatorClick(indicator)}
                    >
                      <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1890ff', marginBottom: '8px' }}>
                        {displayValue}
                      </div>
                      <div style={{ fontSize: '14px', color: '#666' }}>
                        {indicator.label}
                        {indicator.unit && <span style={{ marginLeft: '4px', color: '#999' }}>({indicator.unit})</span>}
                      </div>
                      {progress && (
                        <div style={{ marginTop: '12px' }}>
                          <Progress 
                            percent={progressPercent} 
                            size="small"
                            status={progressPercent >= 100 ? 'success' : 'exception'}
                            strokeColor={progressPercent >= 100 ? undefined : '#ff4d4f'}
                            format={() => `${progressPercent.toFixed(1)}%`}
                          />
                          <div style={{ fontSize: '11px', color: '#999', marginTop: '4px' }}>
                            目标: {typeof progress.target === 'number' ? progress.target.toFixed(4).replace(/\.?0+$/, '') : progress.target}{indicator.unit} ({progress.startYear && progress.endYear ? `${progress.startYear}-${progress.endYear}` : progress.targetYear || '未设置'}年)
                          </div>
                        </div>
                      )}
                      <div style={{ marginTop: '8px', color: '#1890ff', fontSize: '12px' }}>
                        <LineChartOutlined /> 点击查看统计图
                      </div>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          </Card>
        );
      })}
      
      {/* 指标图表模态框 */}
      <Modal
        title={selectedIndicator ? `${selectedIndicator.label} - 统计图表` : '指标统计图表'}
        open={chartVisible}
        onCancel={() => {
          setChartVisible(false);
          setSelectedIndicator(null);
        }}
        footer={null}
        width={900}
        destroyOnClose
      >
        {selectedIndicator && (
          <IndicatorChart
            departmentIds={[department.id]}
            indicator={selectedIndicator}
            indicatorTargets={indicatorTargets}
            currentValue={indicators[selectedIndicator.key]}
            submissions={submissions}
            calculateCurrentValue={calculateCurrentValue}
            getIndicatorDataSource={getIndicatorDataSource}
          />
        )}
      </Modal>
    </div>
  );
};


export default DepartmentView;

